/**
 * Hook global pour notifications nouveaux messages WhatsApp Inbox.
 * - Poll silencieux toutes les 8s (independant de la page inbox)
 * - Detect nouveaux msgs entrants -> notif sonore + browser + tab badge + toast
 * - Actif partout dans l'app (Layout)
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

const POLL_INTERVAL_MS = 8000;
const ORIGINAL_TITLE = 'Boufi OS';
const STORAGE_KEY = 'boufi_inbox_last_seen_ts';

// Petit bip WhatsApp-like genere via WebAudio (pas besoin de fichier audio)
function playPing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    // Two-note "ding" : 880Hz puis 1320Hz
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + i * 0.12;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) {}
}

function ensureBrowserPermission() {
  if (!('Notification' in window)) return Promise.resolve('unsupported');
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Promise.resolve(Notification.permission);
  }
  return Notification.requestPermission();
}

function showBrowserNotif(title, body, onClick) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'boufi-inbox',
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
  } catch (e) {}
}

export function useInboxNotifications({ enabled = true } = {}) {
  const navigate = useNavigate();
  const lastSeenRef = useRef(parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10));
  const intervalRef = useRef(null);
  const titleFlashRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Ask browser permission once
  useEffect(() => {
    if (enabled) ensureBrowserPermission();
  }, [enabled]);

  // Reset on tab focus
  useEffect(() => {
    const onFocus = () => {
      document.title = ORIGINAL_TITLE;
      if (titleFlashRef.current) {
        clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const flashTitle = useCallback((count) => {
    if (titleFlashRef.current) clearInterval(titleFlashRef.current);
    let toggle = false;
    const baseTitle = `(${count}) Nouveau msg WhatsApp - Boufi OS`;
    document.title = baseTitle;
    titleFlashRef.current = setInterval(() => {
      if (!document.hidden) {
        document.title = ORIGINAL_TITLE;
        clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
        return;
      }
      toggle = !toggle;
      document.title = toggle ? `🔔 ${baseTitle}` : baseTitle;
    }, 1200);
  }, []);

  const check = useCallback(async () => {
    if (!localStorage.getItem('bouffi_token')) return;
    try {
      const r = await api.get('/whatsapp/conversations', { params: { status: 'all', limit: 30 } });
      const items = r.data.items || [];
      // Detect newer-than-lastSeen incoming msgs
      const lastSeen = lastSeenRef.current;
      const newOnes = items.filter(c => {
        if ((c.unread_count || 0) === 0) return false;
        const ts = new Date(c.last_message_at || 0).getTime();
        return ts > lastSeen;
      });
      const totalUnread = r.data.total_unread || items.reduce((s, c) => s + (c.unread_count || 0), 0);
      setUnreadCount(totalUnread);

      if (newOnes.length > 0 && lastSeen > 0) {
        // Ignore first ever check (lastSeen == 0) to avoid spam at app load
        playPing();
        const top = newOnes[0];
        toast.success(`💬 ${top.contact_name}`, {
          description: top.last_message?.slice(0, 80) || 'Nouveau message',
          duration: 6000,
          action: {
            label: 'Voir',
            onClick: () => navigate('/inbox'),
          },
        });
        showBrowserNotif(
          `💬 ${top.contact_name}`,
          top.last_message?.slice(0, 120) || 'Nouveau message WhatsApp',
          () => navigate('/inbox')
        );
        if (document.hidden) flashTitle(totalUnread);
      }

      // Update lastSeen
      const maxTs = items.reduce((m, c) => Math.max(m, new Date(c.last_message_at || 0).getTime()), 0);
      if (maxTs > lastSeenRef.current) {
        lastSeenRef.current = maxTs;
        localStorage.setItem(STORAGE_KEY, String(maxTs));
      }
    } catch (e) {
      // silent
    }
  }, [navigate, flashTitle]);

  useEffect(() => {
    if (!enabled) return;
    check(); // initial
    intervalRef.current = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [enabled, check]);

  return { unreadCount };
}
